"use client";

import { useEffect } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { Loader2 } from "lucide-react";
import { useDetailActionContextOptional } from "@/contexts/DetailActionContext";
import type { FindDecisionMakersModalData } from "@/lib/modals/data/modal-data-types";
import { resolveCompanyModalContext } from "./modal-company-context";
import type { ContextContactFallback } from "./modal-company-context";
import { useFindDecisionMakersModal } from "./find-decision-makers/useFindDecisionMakersModal";
import { NoCompanyStep } from "./find-decision-makers/NoCompanyStep";
import { ContactSelectionStep } from "./find-decision-makers/ContactSelectionStep";
import { ImportResultsStep } from "./find-decision-makers/ImportResultsStep";

export function FindDecisionMakersModal({
  contextContact,
}: {
  contextContact?: ContextContactFallback;
}) {
  return (
    <ModalContainer<FindDecisionMakersModalData> modalId={MODAL_IDS.companies.findDecisionMakers}>
      {({ isOpen, data, onClose }) => (
        <FindDecisionMakersModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
          contextContact={contextContact}
        />
      )}
    </ModalContainer>
  );
}

function FindDecisionMakersModalContent({
  isOpen,
  data,
  onClose,
  contextContact,
}: {
  isOpen: boolean;
  data?: FindDecisionMakersModalData;
  onClose: () => void;
  contextContact?: ContextContactFallback;
}) {
  const detailContext = useDetailActionContextOptional();

  const {
    step,
    setStep,
    contacts,
    selectedContacts,
    importResults,
    loading,
    error,
    setCompanyId,
    setCompanyData,
    loadDecisionMakers,
    toggleContactSelection,
    toggleSelectAll,
    handleImportContacts,
    handleClose,
  } = useFindDecisionMakersModal({ onClose });

  useEffect(() => {
    if (!isOpen) return;

    const resolved = resolveCompanyModalContext({
      modalContact: data?.contact,
      modalCompany: data?.company,
      contextContact,
      detailItem: detailContext?.item,
      detailEntityName: detailContext?.pageConfig?.entityName,
    });

    const cId = resolved.companyId;

    if (cId) {
      setCompanyId(cId);
      if (resolved.companyData) {
        setCompanyData({
          id: resolved.companyData.id,
          name: resolved.companyData.name,
          website_url: "",
          industry: "",
          employee_count: 0,
          annual_revenue: 0,
          city: "",
          state: "",
          country: "",
        });
      }
      setStep("loading");
      void loadDecisionMakers(cId);
    } else {
      setCompanyId(null);
      setStep("no-company");
    }
  }, [isOpen, data, contextContact, detailContext?.item, detailContext?.pageConfig?.entityName]);

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title="Find Decision Makers"
      globalModalId={MODAL_IDS.companies.findDecisionMakers}
    >
      <div className="space-y-4">
        {step === "no-company" && <NoCompanyStep onClose={handleClose} />}

        {step === "loading" && (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {step === "contact-selection" && (
          <ContactSelectionStep
            contacts={contacts}
            selectedContacts={selectedContacts}
            onToggleContact={toggleContactSelection}
            onToggleSelectAll={toggleSelectAll}
            onImport={handleImportContacts}
            onCancel={handleClose}
            loading={loading}
            error={error}
          />
        )}

        {step === "import-results" && (
          <ImportResultsStep results={importResults} onDone={handleClose} />
        )}
      </div>
    </ResponsiveModal>
  );
}
